import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Mail, Phone, User, ArrowRight, Lock, Globe, MapPin } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_category: 'general',  // Default to general
    email: '',
    password: '',
    phone: '',
    website: '',
    google_business_url: '',
    owner_phone_number: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.business_name || !formData.email || !formData.password) {
      toast.error('Business name, email, and password are required');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      // Check for demo agent ID in session storage (set during demo)
      const demoAgentId = sessionStorage.getItem('demo_agent_id');
      
      // Create the business account with free tier
      const businessRes = await axios.post(`${API}/businesses/register-free`, {
        ...formData,
        business_category: formData.business_category || 'general',
        demo_agent_id: demoAgentId || undefined  // Pass demo settings if available
      });
      
      const { business, user, token } = businessRes.data;
      
      // Clear demo session data after successful signup
      if (demoAgentId) {
        sessionStorage.removeItem('demo_agent_id');
      }
      
      // Use AuthContext login to properly set token and user
      login(user, token);
      
      toast.success('Account created! Welcome to Front Desk Pro!');
      
      // Redirect to their dashboard
      navigate(`/business/${business.id}`);
      
    } catch (err) {
      console.error('Signup error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create account';
      
      // Check if it's an email exists error
      if (errorMessage.toLowerCase().includes('email already exists') || errorMessage.toLowerCase().includes('already exists')) {
        setEmailExistsError(true);
        toast.error(
          <div>
            {errorMessage}
            <button 
              onClick={() => navigate('/login')}
              className="ml-2 underline text-primary font-medium"
            >
              Go to Login
            </button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">
              Front<span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-cyan-500">DeskPro</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
              Already have an account? <span className="text-primary font-medium">Sign in</span>
            </Link>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-md">
          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Create your account
            </h1>
            <p className="text-muted-foreground">
              Get started with your AI receptionist in minutes
            </p>
          </div>

          {/* Signup Form */}
          <Card className="p-6 sm:p-8 border-border bg-card">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Business Name */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-business-name"
                    value={formData.business_name}
                    onChange={(e) => setFormData(f => ({ ...f, business_name: e.target.value }))}
                    placeholder="Your Company Name"
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Work Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                    placeholder="you@company.com"
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))}
                    placeholder="Minimum 6 characters"
                    className="pl-10 h-11"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              {/* Phone (Optional) */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-phone"
                    type="tel"
                    value={formData.owner_phone_number}
                    onChange={(e) => setFormData(f => ({ ...f, owner_phone_number: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="pl-10 h-11"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  For call transfers when customers request a human
                </p>
              </div>

              {/* Website (Optional) */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Website
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-website"
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData(f => ({ ...f, website: e.target.value }))}
                    placeholder="yourcompany.com"
                    className="pl-10 h-11"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Just type your domain (e.g. smithplumbing.com) — no https:// needed
                </p>
              </div>

              {/* Google Business Link (Optional) */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Google Business Link
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-google-business"
                    type="text"
                    value={formData.google_business_url}
                    onChange={(e) => setFormData(f => ({ ...f, google_business_url: e.target.value }))}
                    placeholder="Paste your Google Business profile link"
                    className="pl-10 h-11"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Paste the full URL from your Google Business profile (starts with google.com/maps or g.page)
                </p>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 text-white font-semibold text-base"
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Create Free Account
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>

              {/* Login Button - Shows when email already exists */}
              {emailExistsError && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-400 mb-3 text-center">
                    An account with this email already exists.
                  </p>
                  <Button 
                    type="button"
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="w-full h-11 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    Go to Login Page
                  </Button>
                </div>
              )}

              {/* Terms */}
              <p className="text-xs text-center text-muted-foreground">
                By creating an account, you agree to our{' '}
                <a href="/terms" className="text-primary hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
              </p>
            </form>
          </Card>

          {/* Trust Badges */}
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Secure & Encrypted
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No credit card required
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs text-muted-foreground">
          © 2024 Front Desk Pro. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Signup;
